/**
 * Continentes e helpers de exibição de país (bandeira, nome localizado) do
 * admin.
 *
 * Origem: sem issue — commit a0529e97c, 2026-06-11 14:58 +0200 (Sprint 36
 * Fase 2c; nasceu como o universo de 196 países do picker "Adicionar país").
 *
 * Por quê: o universo de países do sistema é a lista da Google Play
 * (`ALL_PLAY_LOCATIONS`, `playStoreCountries.ts`, 225 locais) — o sistema não
 * exclui país, quem exclui é o console da Play (owner, 2026-10-13, #954). A
 * lista própria de 196 (ONU) divergia dela e saiu na #957; aqui ficam só os
 * continentes (as chaves de `PLAY_LOCATIONS_BY_CONTINENT`) e os helpers.
 *
 * Usado por: `features/countries/{CountriesTab,CountryEditDialog,countryRows,
 * playStoreCountries,LanguagesTab}`, `features/billing/{AddonCountriesPicker,addonRow}`,
 * `features/psp/{PspListPage,PspEditorPage}` (bandeira e nome do país) — admin web.
 * `routes/AddonsPage.tsx` só usa a chave i18n `continents.*`, não importa este arquivo.
 *
 * Dicas: `EU` (União Europeia) é um atalho, não um continente — vem de
 * `EU_COUNTRIES` em @etiquetachef/types (fonte única).
 *
 * Issues: #954 (lista da Play), #957 (aba Países com os 225).
 *
 * Histórico:
 *  - 2026-10-13 11:20 +0200 — (#957) saem `COUNTRIES_BY_CONTINENT` e
 *    `ALL_WORLD_COUNTRIES` (196 países); o universo passa a ser a lista da Play.
 *  - 2026-10-29 10:20 +0100 — (#1078, F2 da #960) `northAmerica` dividida em `northAmerica`,
 *    `centralAmerica` e `caribbean` (8 grupos).
 *  - 2026-10-29 10:57 +0100 — (#1078, auditoria F2) "Usado por" conferido com `rg`: a PSP usa a
 *    bandeira/nome (não os continentes), e `LanguagesTab` também importa.
 */
export const CONTINENT_KEYS = [
  'africa',
  'northAmerica',
  // #1078 (F2 da #960): a América do Norte da Play juntava o Caribe e a América Central; o
  // mapa país → região (#960) exibe os grupos separados.
  'centralAmerica',
  'caribbean',
  'southAmerica',
  'asia',
  'europe',
  'oceania',
] as const;
export type ContinentKey = (typeof CONTINENT_KEYS)[number];

/** Emoji de bandeira a partir do ISO-2 (par de regional indicator symbols). */
export function flagEmoji(code: string): string {
  if (!/^[A-Za-z]{2}$/.test(code)) return '';
  return code
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

/** Nome localizado do país; cai no próprio código se o runtime não resolver. */
export function countryDisplayName(code: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}
